import React from "react";
import {
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignMiddleOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import { typography, textColors, surfaces, borders, interactive } from "../../../design-system/tokens";

// TextPositionField renders separate horizontal and vertical alignment controls.
export const TextPositionField = ({ value, onChange, label, help }) => {
  const [vertical = "middle", horizontal = "center"] = value?.split("-") || [];

  const iconSize = 18;

  const horizontalChoices = [
    { value: "left", label: "Align Left", icon: <AlignLeftOutlined style={{ fontSize: iconSize }} /> },
    { value: "center", label: "Align Center", icon: <AlignCenterOutlined style={{ fontSize: iconSize }} /> },
    { value: "right", label: "Align Right", icon: <AlignRightOutlined style={{ fontSize: iconSize }} /> },
    { value: "full", label: "Full Width", icon: <FullWidthIcon size={iconSize} /> },
  ];

  const verticalChoices = [
    { value: "top", label: "Top", icon: <VerticalAlignTopOutlined style={{ fontSize: iconSize }} /> },
    { value: "middle", label: "Middle", icon: <VerticalAlignMiddleOutlined style={{ fontSize: iconSize }} /> },
    { value: "bottom", label: "Bottom", icon: <VerticalAlignBottomOutlined style={{ fontSize: iconSize }} /> },
  ];

  const makeClass = (selected) =>
    `w-9 h-9 rounded-lg border transition-all flex items-center justify-center ${
      selected
        ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
        : `${surfaces.tertiary} ${textColors.primary} ${borders.default} hover:bg-gray-100 dark:hover:bg-dark-700`
    }`;

  const updateHorizontal = (h) => onChange(`${vertical}-${h}`);
  const updateVertical = (v) => onChange(`${v}-${horizontal}`);

  return (
    <div className="field-wrapper">
      {label && <label className={`block mb-3 ${typography.label} ${textColors.secondary}`}>{label}</label>}

      <div className="space-y-3">
        {/* Horizontal alignment */}
        <div>
          <div className={`mb-1.5 ${typography.caption} ${textColors.muted}`}>Horizontal</div>
          <div className={`inline-flex gap-1 p-1 rounded-xl border ${borders.default} ${surfaces.secondary}`}>
            {horizontalChoices.map((choice) => (
              <button
                type="button"
                key={choice.value}
                onClick={() => updateHorizontal(choice.value)}
                className={makeClass(horizontal === choice.value)}
                title={choice.label}
                aria-label={choice.label}
                aria-pressed={horizontal === choice.value}
              >
                {choice.icon}
              </button>
            ))}
          </div>
        </div>

        {/* Vertical alignment */}
        <div>
          <div className={`mb-1.5 ${typography.caption} ${textColors.muted}`}>Vertical</div>
          <div className={`inline-flex gap-1 p-1 rounded-xl border ${borders.default} ${surfaces.secondary}`}>
            {verticalChoices.map((choice) => (
              <button
                type="button"
                key={choice.value}
                onClick={() => updateVertical(choice.value)}
                className={makeClass(vertical === choice.value)}
                title={choice.label}
                aria-label={choice.label}
                aria-pressed={vertical === choice.value}
              >
                {choice.icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      {help && <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
};

// Custom full-width icon re-styled to match Ant Design icons' stroke and spacing
const FullWidthIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    aria-hidden="true"
  >
    <line x1="2" y1="2.5" x2="18" y2="2.5" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="2" y1="7.5" x2="18" y2="7.5" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="2" y1="12.5" x2="18" y2="12.5" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="2" y1="17.5" x2="18" y2="17.5" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
